import { useEffect, useRef, useState } from 'react';
import { useId } from 'react';
import { TrendingUp, ShoppingBag, Zap, ArrowUpRight } from 'lucide-react';

/* ─── Stats data ────────────────────────────────────────── */
const STATS = [
  {
    stat: '+42%',
    label: 'Increase in conversion rate.',
    desc: 'We redesigned the journey around the moments that actually influence a customer\'s decision to buy.',
    icon: TrendingUp,
    primary: true,
  },
  {
    stat: '+30%',
    label: 'Increase in average order value.',
    desc: 'A more considered commerce experience that made it easier for customers to discover and buy more.',
    icon: ShoppingBag,
    primary: false,
  },
  {
    stat: '+40%',
    label: 'Improvement in checkout performance.',
    desc: 'We identified where customers were dropping off and rebuilt the path to purchase around fewer points of friction.',
    icon: Zap,
    primary: false,
  },
];

/* ─── SVG Grid Pattern ──────────────────────────────────── */
function GridPattern({ width, height, x, y, squares, ...props }) {
  const patternId = useId();
  return (
    <svg aria-hidden="true" {...props}>
      <defs>
        <pattern id={patternId} width={width} height={height}
          patternUnits="userSpaceOnUse" x={x} y={y}>
          <path d={`M.5 ${height}V.5H${width}`} fill="none" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" strokeWidth={0} fill={`url(#${patternId})`} />
      {squares && (
        <svg x={x} y={y} style={{ overflow: 'visible' }}>
          {squares.map(([sx, sy]) => (
            <rect strokeWidth="0" key={`${sx}-${sy}`}
              width={width + 1} height={height + 1}
              x={sx * width} y={sy * height} />
          ))}
        </svg>
      )}
    </svg>
  );
}

function GridOverlay() {
  const squares = [[7,2],[9,1],[11,3],[8,5],[10,4]];
  return (
    <div style={{
      pointerEvents: 'none', position: 'absolute',
      left: '50%', top: 0, marginLeft: '-80px', marginTop: '-8px',
      height: '100%', width: '100%',
      WebkitMaskImage: 'linear-gradient(white,transparent)',
      maskImage: 'linear-gradient(white,transparent)',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(farthest-side at top,rgba(255,255,255,0.06),transparent)',
        WebkitMaskImage: 'radial-gradient(farthest-side at top,white,transparent)',
        maskImage: 'radial-gradient(farthest-side at top,white,transparent)',
      }}>
        <GridPattern width={20} height={20} x="-12" y="4"
          squares={squares}
          style={{
            position:'absolute',inset:0,height:'100%',width:'100%',
            mixBlendMode:'overlay',
            fill:'rgba(255,255,255,0.09)',
            stroke:'rgba(255,255,255,0.09)',
          }} />
      </div>
    </div>
  );
}

/* ─── Animated counter ──────────────────────────────────── */
function AnimatedStat({ target, suffix = '%', prefix = '+' }) {
  const [val, setVal] = useState(0);
  const triggered = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !triggered.current) {
        triggered.current = true;
        const steps = 60;
        const dur = 1600;
        let i = 0;
        const tick = setInterval(() => {
          i++;
          const p = 1 - Math.pow(1 - i / steps, 3);
          setVal(Math.floor(target * p));
          if (i >= steps) { clearInterval(tick); setVal(target); }
        }, dur / steps);
      }
    }, { threshold: 0.4 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{prefix}{val}{suffix}</span>;
}

/* ─── Main Component ────────────────────────────────────── */
export default function ImpactStats() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap');

        .is-section {
          background: #080808;
          font-family: 'Bricolage Grotesque', sans-serif;
          padding: clamp(56px,9vh,112px) clamp(20px,6vw,96px);
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        /* ── Header ── */
        .is-section-heading {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem);
          font-weight: 800;
          letter-spacing: -0.035em;
          line-height: 1.08;
          color: #ffffff;
          text-align: center;
          text-transform: none;
          margin: 0 0 clamp(32px, 5vh, 52px);
        }
        .is-section-heading em {
          font-style: normal;
          color: #ec2b28;
        }

        /* ── Bento grid ── */
        .is-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          grid-template-rows: auto auto;
          gap: 14px;
        }

        /* Card base */
        .is-card {
          position: relative;
          border-radius: 22px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.07);
          padding: clamp(24px,3vw,38px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
          cursor: default;
        }
        .is-card:hover {
          border-color: rgba(236,43,40,0.3);
          transform: translateY(-3px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.45);
        }

        /* Primary card (first) */
        .is-card-primary {
          grid-column: 1;
          grid-row: 1 / 3;
          background: linear-gradient(145deg,#1c0a0a 0%,#0d0d0d 100%);
          border-color: rgba(236,43,40,0.18);
        }

        /* Secondary cards */
        .is-card-secondary {
          background: linear-gradient(160deg,#141414 0%,#0a0a0a 100%);
        }

        /* Card inner — content full width, stat top-right */
        .is-card-body {
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 2;
          flex: 1;
          width: 100%;
        }

        /* Top row: icon+tag on left, stat on right */
        .is-card-top-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          margin-bottom: 16px;
        }

        .is-card-top-left {
          display: flex;
          flex-direction: column;
        }

        .is-card-left {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .is-card-right {
          display: none; /* no longer used */
        }

        /* Stripe texture for primary card */
        .is-card-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            45deg,
            rgba(236,43,40,0.04) 0px 1px,
            transparent 1px 10px
          );
          mask-image: radial-gradient(ellipse 80% 50% at 100% 0%,#000 70%,transparent 110%);
          -webkit-mask-image: radial-gradient(ellipse 80% 50% at 100% 0%,#000 70%,transparent 110%);
          pointer-events: none;
        }

        /* icon wrap */
        .is-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(236,43,40,0.12);
          border: 1px solid rgba(236,43,40,0.22);
          margin-bottom: 20px;
          position: relative;
          z-index: 2;
          flex-shrink: 0;
        }

        /* tag pill */
        .is-tag {
          display: inline-block;
          font-size: 0.58rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(236,43,40,0.7);
          background: rgba(236,43,40,0.1);
          border: 1px solid rgba(236,43,40,0.18);
          border-radius: 100px;
          padding: 4px 12px;
          margin-bottom: 18px;
          align-self: flex-start;
          position: relative;
          z-index: 2;
        }

        /* stat number — bottom-right */
        .is-stat {
          font-size: clamp(3rem, 6vw, 5rem);
          font-weight: 900;
          line-height: 1;
          letter-spacing: -0.04em;
          color: #ec2b28;
          white-space: nowrap;
          position: relative;
          z-index: 2;
        }

        /* label */
        .is-label {
          font-size: clamp(0.85rem,1.3vw,1.05rem);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.35;
          margin: 0 0 8px;
          position: relative;
          z-index: 2;
          width: 100%;
        }

        /* desc */
        .is-desc {
          font-size: clamp(0.78rem,1.1vw,0.88rem);
          font-weight: 500;
          color: #d1d5db;
          line-height: 1.65;
          margin: 0;
          position: relative;
          z-index: 2;
          width: 100%;
        }

        /* case study link */
        .is-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          color: #ffffff;
          text-decoration: none;
          text-transform: uppercase;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.06);
          border-radius: 8px;
          padding: 8px 16px;
          align-self: flex-start;
          position: relative;
          z-index: 2;
          transition: color 0.2s, border-color 0.2s, background 0.2s, transform 0.2s;
        }
        .is-link:hover {
          color: #ffffff;
          border-color: rgba(236,43,40,0.6);
          background: rgba(236,43,40,0.18);
          transform: translateY(-2px);
        }

        /* ── Bottom CTA row ── */
        .is-cta-row {
          margin-top: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(90deg,#141414 0%,#0d0d0d 100%);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 18px;
          padding: clamp(20px,3vw,32px) clamp(24px,4vw,48px);
          gap: 20px;
          flex-wrap: wrap;
        }
        .is-cta-text {
          font-size: clamp(1rem,2.5vw,1.6rem);
          font-weight: 800;
          color: #fff;
          letter-spacing: -0.025em;
          line-height: 1.2;
          margin: 0;
        }
        .is-cta-text em { font-style: normal; color: #ec2b28; }
        .is-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #fff;
          text-decoration: none;
          background: #ec2b28;
          border-radius: 8px;
          padding: 12px 24px;
          white-space: nowrap;
          flex-shrink: 0;
          transition: background 0.2s, transform 0.2s;
        }
        .is-cta-btn:hover { background: #c9211e; transform: translateY(-2px); }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .is-grid {
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto;
          }
          .is-card-primary {
            grid-column: 1 / 3;
            grid-row: 1;
          }
        }
        @media (max-width: 560px) {
          .is-section { padding-left: 16px; padding-right: 16px; }
          .is-grid { grid-template-columns: 1fr; }
          .is-card-primary { grid-column: 1; grid-row: auto; }
          .is-cta-row { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <section className="is-section">
        <h2 className="is-section-heading">Results that speak</h2>

        <div className="is-grid">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            const num = parseInt(s.stat.replace(/\D/g,''));
            return (
              <div key={i} className={`is-card ${s.primary ? 'is-card-primary' : 'is-card-secondary'}`}>
                <GridOverlay />

                <div className="is-card-body">
                  {/* TOP ROW — icon+tag left, big stat top-right */}
                  <div className="is-card-top-row">
                    <div className="is-card-top-left">
                      <div className="is-icon-wrap">
                        <Icon size={18} strokeWidth={1.8} color="#ec2b28" />
                      </div>
                      <span className="is-tag">Case Study</span>
                    </div>
                    <div className="is-stat">
                      <AnimatedStat target={num} prefix="+" suffix="%" />
                    </div>
                  </div>

                  {/* CONTENT — label + desc use full width below */}
                  <div className="is-card-left">
                    <p className="is-label">{s.label}</p>
                    <p className="is-desc">{s.desc}</p>
                  </div>
                </div>

                {/* case study link */}
                <a href="#works" className="is-link">
                  View the case study <ArrowUpRight size={13} strokeWidth={2.2} />
                </a>
              </div>
            );
          })}
        </div>

        {/* ── Bottom CTA row ── */}
        <div className="is-cta-row">
          <p className="is-cta-text">
            Good work looks good.<br />
            <em>Great work moves the business.</em>
          </p>
          <a href="#works" className="is-cta-btn">
            Explore all our work <ArrowUpRight size={14} strokeWidth={2.5} />
          </a>
        </div>

      </section>
    </>
  );
}
